import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, TrendingUp, TrendingDown, Calculator, ClipboardList, Link2, FileSpreadsheet, FileText, Check, ChevronsUpDown } from "lucide-react";
import { formatCurrency, formatPercent } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { API } from "@/lib/api";


const initialFormState = {
  name: "",
  description: "",
  service_type: "servico",
  sale_price: 0,
  // Variable expenses (%)
  tax_percent: 11.0,
  receipt_fee_percent: 13.0,
  commission_percent: 0,
  delivery_percent: 0,
  packaging_percent: 0,
  loss_percent: 1.0,
  other_expense_1_name: "",
  other_expense_1_percent: 0,
  other_expense_2_name: "",
  other_expense_2_percent: 0,
  other_expense_3_name: "",
  other_expense_3_percent: 0,
  other_expense_4_name: "",
  other_expense_4_percent: 0,
  // Unit costs (R$)
  purchase_value: 0,
  third_party_service: 0,
  materials: 0,
  supplies: 0,
  ingredients: 0,
  other_cost_1_name: "",
  other_cost_1_value: 0,
  other_cost_2_name: "",
  other_cost_2_value: 0,
  other_cost_3_name: "",
  other_cost_3_value: 0,
  other_cost_4_name: "",
  other_cost_4_value: 0,
  other_cost_5_name: "",
  other_cost_5_value: 0,
  // Technical sheet link
  technical_sheet_id: null,
};

function TechnicalSheetCombobox({ sheets, value, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = sheets.find(s => s.id === value);

  const filtered = sheets.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-72 justify-between text-left font-normal"
          data-testid="select-technical-sheet"
        >
          <span className="truncate">
            {selected ? `${selected.name} (${formatCurrency(selected.total_cost)})` : "Vincular Ficha Técnica"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar ficha tecnica..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>Nenhuma ficha encontrada.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => { onSelect("none"); setOpen(false); setSearch(""); }}>
                <Check className={`mr-2 h-4 w-4 ${!value || value === "none" ? "opacity-100" : "opacity-0"}`} />
                Nenhuma ficha tecnica
              </CommandItem>
              {filtered.map((sheet) => (
                <CommandItem key={sheet.id} onSelect={() => { onSelect(sheet.id); setOpen(false); setSearch(""); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === sheet.id ? "opacity-100" : "opacity-0"}`} />
                  {sheet.name} ({formatCurrency(sheet.total_cost)})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function CostSheetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState(initialFormState);
  const [calculation, setCalculation] = useState(null);
  const [technicalSheets, setTechnicalSheets] = useState([]);
  const [selectedTechnicalSheet, setSelectedTechnicalSheet] = useState(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTechnicalSheets();
    if (isEditing) {
      fetchSheet();
    } else {
      fetchDefaults();
    }
  }, [id]);

  // Ficha nova comeca com os valores padrao salvos em Configuracoes. Antes a
  // tela de Configuracoes prometia isso, mas a ficha ignorava e usava 11/13/1 fixos.
  const fetchDefaults = async () => {
    try {
      const res = await axios.get(`${API}/config`);
      setFormData((prev) => ({
        ...prev,
        tax_percent: res.data.default_tax_percent ?? prev.tax_percent,
        receipt_fee_percent: res.data.default_receipt_fee_percent ?? prev.receipt_fee_percent,
        loss_percent: res.data.default_loss_percent ?? prev.loss_percent,
      }));
    } catch (error) {
      console.error("Error fetching config defaults:", error);
    }
  };

  const fetchTechnicalSheets = async () => {
    try {
      const res = await axios.get(`${API}/technical-sheets`);
      setTechnicalSheets(res.data);
    } catch (error) {
      console.error("Error fetching technical sheets:", error);
    }
  };

  const fetchSheet = async () => {
    try {
      const res = await axios.get(`${API}/cost-sheets/${id}`);
      const sheetData = res.data;
      
      // If has linked technical sheet, fetch latest values and sync
      if (sheetData.technical_sheet_id) {
        try {
          const techRes = await axios.get(`${API}/technical-sheets/${sheetData.technical_sheet_id}`);
          const techSheet = techRes.data;
          setSelectedTechnicalSheet(techSheet);
          // Always sync with latest technical sheet values
          sheetData.purchase_value = techSheet.main_product_cost || 0;
          sheetData.supplies = techSheet.supplies_cost || 0;
        } catch (e) {
          console.error("Technical sheet not found");
        }
      }
      
      setFormData(sheetData);
    } catch (error) {
      toast.error("Erro ao carregar ficha de custo");
      navigate("/cost-sheets");
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = useCallback(async (data) => {
    try {
      const res = await axios.post(`${API}/calculate-pricing`, data);
      setCalculation(res.data);
    } catch (error) {
      console.error("Error calculating pricing:", error);
    }
  }, []);

  // Recalculate on form change
  useEffect(() => {
    const timer = setTimeout(() => {
      calculatePricing(formData);
    }, 300);
    return () => clearTimeout(timer);
  }, [formData, calculatePricing]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (field, value) => {
    // Keep as string in form to avoid leading zeros
    // Remove leading zeros except for decimals like "0.5"
    let cleaned = value.replace(/^0+(?=\d)/, '');
    if (cleaned === '') cleaned = '';
    const numValue = parseFloat(cleaned) || 0;
    handleChange(field, numValue);
  };

  const handleTechnicalSheetSelect = (sheetId) => {
    if (!sheetId || sheetId === "none") {
      setSelectedTechnicalSheet(null);
      setFormData((prev) => ({
        ...prev,
        technical_sheet_id: null,
      }));
      return;
    }
    
    const sheet = technicalSheets.find((s) => s.id === sheetId);
    if (sheet) {
      setSelectedTechnicalSheet(sheet);
      // main_product_cost -> Valor de Compra (purchase_value)
      // supplies_cost -> Insumos (supplies)
      setFormData((prev) => ({
        ...prev,
        technical_sheet_id: sheet.id,
        purchase_value: sheet.main_product_cost || 0,
        supplies: sheet.supplies_cost || 0,
      }));
      toast.success(`Ficha técnica vinculada! Valor de Compra: ${formatCurrency(sheet.main_product_cost || 0)}, Insumos: ${formatCurrency(sheet.supplies_cost || 0)}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/cost-sheets/${id}`, formData);
        toast.success("Ficha de custo atualizada com sucesso");
      } else {
        await axios.post(`${API}/cost-sheets`, formData);
        toast.success("Ficha de custo criada com sucesso");
      }
      navigate("/cost-sheets");
    } catch (error) {
      toast.error("Erro ao salvar ficha de custo");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="cost-sheet-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/cost-sheets")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {isEditing ? "Editar Ficha de Custo" : "Nova Ficha de Custo"}
          </h1>
          <p className="text-slate-500 mt-1">
            Campos em <span className="bg-amber-100 px-1 rounded">amarelo</span> são editáveis
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Form Inputs */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                  Informações Básicas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Ex: Limpeza de Pele"
                      className="editable-field"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service_type">Tipo</Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(v) => handleChange("service_type", v)}
                    >
                      <SelectTrigger className="editable-field" data-testid="input-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="servico">Serviço</SelectItem>
                        <SelectItem value="produto">Produto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Descrição opcional"
                    className="editable-field"
                    data-testid="input-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sale_price" className="text-lg font-semibold">
                    Preço de Venda (R$)
                  </Label>
                  <Input
                    id="sale_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.sale_price || ""}
                    onChange={(e) => handleNumberChange("sale_price", e.target.value)}
                    className="editable-field font-mono text-xl h-12"
                    data-testid="input-sale-price"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Variable Expenses */}
            <Card>
              <CardHeader className="pb-4 border-b border-slate-100">
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                  Custos e Despesas Variáveis
                </CardTitle>
                <p className="text-sm text-slate-500">Percentuais sobre o preço de venda</p>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Impostos (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.tax_percent || ""}
                      onChange={(e) => handleNumberChange("tax_percent", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-tax"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Taxa Recebimento (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.receipt_fee_percent || ""}
                      onChange={(e) => handleNumberChange("receipt_fee_percent", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-receipt-fee"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Comissões (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.commission_percent || ""}
                      onChange={(e) => handleNumberChange("commission_percent", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-commission"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Entrega/Frete (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.delivery_percent || ""}
                      onChange={(e) => handleNumberChange("delivery_percent", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-delivery"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Embalagem/Kit Venda (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.packaging_percent || ""}
                      onChange={(e) => handleNumberChange("packaging_percent", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-packaging"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Perda/Extravio/Não Receb. (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.loss_percent || ""}
                      onChange={(e) => handleNumberChange("loss_percent", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-loss"
                    />
                  </div>
                </div>

                {/* Other expenses */}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <Label className="text-sm text-slate-500 mb-3 block">Outras Despesas</Label>
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={`Outro ${i}`}
                          value={formData[`other_expense_${i}_name`]}
                          onChange={(e) => handleChange(`other_expense_${i}_name`, e.target.value)}
                          className="editable-field"
                        />
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="%"
                          value={formData[`other_expense_${i}_percent`] || ""}
                          onChange={(e) => handleNumberChange(`other_expense_${i}_percent`, e.target.value)}
                          className="editable-field font-mono"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Unit Costs */}
            <Card>
              <CardHeader className="pb-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                      Custo Unitário
                    </CardTitle>
                    <p className="text-sm text-slate-500">Valores fixos em Reais (R$)</p>
                  </div>
                  {technicalSheets.length > 0 && (
                    <div className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-teal-600" />
                      <TechnicalSheetCombobox
                        sheets={technicalSheets}
                        value={formData.technical_sheet_id}
                        onSelect={handleTechnicalSheetSelect}
                      />
                    </div>
                  )}
                </div>
                {selectedTechnicalSheet && (
                  <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-teal-600" />
                        <span className="text-sm text-teal-700">
                          Vinculado: <strong>{selectedTechnicalSheet.name}</strong>
                        </span>
                      </div>
                      <Link 
                        to={`/technical-sheets/${selectedTechnicalSheet.id}`}
                        className="text-teal-600 hover:underline text-xs"
                      >
                        Editar Ficha
                      </Link>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-blue-50 rounded">
                        <span className="text-blue-600">Valor de Compra:</span>
                        <span className="font-mono font-bold text-blue-800 ml-1">
                          {formatCurrency(selectedTechnicalSheet.main_product_cost || 0)}
                        </span>
                      </div>
                      <div className="p-2 bg-slate-100 rounded">
                        <span className="text-slate-600">Insumos:</span>
                        <span className="font-mono font-bold text-slate-800 ml-1">
                          {formatCurrency(selectedTechnicalSheet.supplies_cost || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className={selectedTechnicalSheet ? "text-blue-700 font-medium" : ""}>
                        Valor de Compra (R$)
                      </Label>
                      {selectedTechnicalSheet && (
                        <span className="text-xs text-blue-500">← da ficha técnica</span>
                      )}
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.purchase_value || ""}
                      onChange={(e) => {
                        handleNumberChange("purchase_value", e.target.value);
                        if (selectedTechnicalSheet) {
                          setSelectedTechnicalSheet(null);
                          setFormData((prev) => ({ ...prev, technical_sheet_id: null }));
                        }
                      }}
                      className={`font-mono ${selectedTechnicalSheet ? 'bg-blue-50 border-blue-200' : 'editable-field'}`}
                      data-testid="input-purchase"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Serviço de Terceiros (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.third_party_service || ""}
                      onChange={(e) => handleNumberChange("third_party_service", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-third-party"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Materiais (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.materials || ""}
                      onChange={(e) => handleNumberChange("materials", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-materials"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className={selectedTechnicalSheet ? "text-slate-700 font-medium" : ""}>
                        Insumos (R$)
                      </Label>
                      {selectedTechnicalSheet && (
                        <span className="text-xs text-slate-500">← da ficha técnica</span>
                      )}
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.supplies || ""}
                      onChange={(e) => {
                        handleNumberChange("supplies", e.target.value);
                        if (selectedTechnicalSheet) {
                          setSelectedTechnicalSheet(null);
                          setFormData((prev) => ({ ...prev, technical_sheet_id: null }));
                        }
                      }}
                      className={`font-mono ${selectedTechnicalSheet ? 'bg-slate-100 border-slate-300' : 'editable-field'}`}
                      data-testid="input-supplies"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ingredientes (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.ingredients || ""}
                      onChange={(e) => handleNumberChange("ingredients", e.target.value)}
                      className="editable-field font-mono"
                      data-testid="input-ingredients"
                    />
                  </div>
                </div>

                {/* Other costs */}
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <Label className="text-sm text-slate-500 mb-3 block">Outros Custos</Label>
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder={`Outro ${i}`}
                          value={formData[`other_cost_${i}_name`]}
                          onChange={(e) => handleChange(`other_cost_${i}_name`, e.target.value)}
                          className="editable-field"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="R$"
                          value={formData[`other_cost_${i}_value`] || ""}
                          onChange={(e) => handleNumberChange(`other_cost_${i}_value`, e.target.value)}
                          className="editable-field font-mono"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Calculation Results */}
          <div className="space-y-6">
            {/* Sticky calculation card */}
            <div className="lg:sticky lg:top-6">
              <Card className="border-2 border-teal-200 bg-gradient-to-b from-teal-50 to-white">
                <CardHeader className="pb-4 border-b border-teal-100">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-teal-600" />
                    <CardTitle className="text-lg text-teal-800" style={{ fontFamily: 'Manrope' }}>
                      Resultado da Precificação
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {calculation ? (
                    <>
                      {/* Sale Price */}
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <p className="text-sm text-slate-500">Preço de Venda</p>
                        <p className="text-2xl font-bold font-mono text-slate-900" data-testid="calc-sale-price">
                          {formatCurrency(calculation.sale_price)}
                        </p>
                      </div>

                      {/* Variable Expenses */}
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <p className="text-sm text-slate-500">Impostos e Despesas de Venda</p>
                        <p className="text-xl font-bold font-mono text-slate-900" data-testid="calc-expenses">
                          {formatCurrency(calculation.total_variable_expenses)}
                        </p>
                        <p className="text-sm text-slate-500 font-mono">
                          ({formatPercent(calculation.total_variable_expenses_percent)} do preço)
                        </p>
                      </div>

                      {/* Unit Cost */}
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <p className="text-sm text-slate-500">Custo Unitário</p>
                        <p className="text-xl font-bold font-mono text-slate-900" data-testid="calc-unit-cost">
                          {formatCurrency(calculation.total_unit_cost)}
                        </p>
                      </div>

                      {/* Contribution Margin */}
                      <div className={`p-4 rounded-lg border-2 ${
                        calculation.is_profitable 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-rose-50 border-rose-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {calculation.is_profitable ? (
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-rose-600" />
                          )}
                          <p className={`text-sm font-medium ${
                            calculation.is_profitable ? 'text-emerald-700' : 'text-rose-700'
                          }`}>
                            Margem de Contribuição
                          </p>
                        </div>
                        <p className={`text-2xl font-bold font-mono ${
                          calculation.is_profitable ? 'text-emerald-700' : 'text-rose-700'
                        }`} data-testid="calc-margin">
                          {formatCurrency(calculation.contribution_margin)}
                        </p>
                        <p className={`text-lg font-mono ${
                          calculation.is_profitable ? 'text-emerald-600' : 'text-rose-600'
                        }`} data-testid="calc-margin-percent">
                          {formatPercent(calculation.contribution_margin_percent)}
                        </p>
                      </div>

                      {/* Breakdown details */}
                      <div className="text-xs text-slate-500 space-y-1 p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-slate-600 mb-2">Detalhamento:</p>
                        <p>Impostos: {formatCurrency(calculation.expense_breakdown.impostos)}</p>
                        <p>Taxa Receb.: {formatCurrency(calculation.expense_breakdown.taxa_recebimento)}</p>
                        <p>Comissões: {formatCurrency(calculation.expense_breakdown.comissoes)}</p>
                        <p>Entrega: {formatCurrency(calculation.expense_breakdown.entrega_frete)}</p>
                        <p>Embalagem: {formatCurrency(calculation.expense_breakdown.embalagem)}</p>
                        <p>Perdas: {formatCurrency(calculation.expense_breakdown.perda_extravio)}</p>
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-500 text-center py-8">
                      Preencha os valores para ver o cálculo
                    </p>
                  )}

                  {/* Save Button */}
                  <Button
                    type="submit"
                    className="w-full bg-teal-600 hover:bg-teal-700 h-12"
                    disabled={saving}
                    data-testid="save-sheet-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Salvando..." : isEditing ? "Atualizar Ficha" : "Criar Ficha"}
                  </Button>

                  {/* Export buttons - only show when editing */}
                  {isEditing && (
                    <div className="pt-4 border-t border-slate-200 space-y-2">
                      <p className="text-xs text-slate-500 font-medium">Exportar</p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => window.open(`${API}/cost-sheets/${id}/export-excel`, '_blank')}
                          data-testid="export-excel-btn"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Excel
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => window.print()}
                          data-testid="export-pdf-btn"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
